import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, BarChart3, DollarSign, GitCompareArrows, Layers, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  Legend,
  ComposedChart,
  Line,
  LineChart,
} from 'recharts';

interface QuarterData {
  date: string;
  period: string;
  calendarYear: string;
  eps: number;
  epsActual: number | null;
  epsEstimated: number | null;
  surprisePct: number | null;
  beat: boolean | null;
  revenue: number;
  revenueEstimated: number | null;
  revenueSurprisePct: number | null;
  revenueBeat: boolean | null;
}

interface EarningsSurpriseChartProps {
  tickers: string[];
}

const COMPARE_COLORS = [
  'hsl(var(--primary))',
  'hsl(142 76% 36%)',
  'hsl(0 84% 60%)',
  'hsl(47 96% 53%)',
  'hsl(262 83% 58%)',
  'hsl(199 89% 48%)',
  'hsl(25 95% 53%)',
  'hsl(330 81% 60%)',
];

const EarningsSurpriseChart = ({ tickers }: EarningsSurpriseChartProps) => {
  const [selectedTicker, setSelectedTicker] = useState<string>('');
  const [quarterData, setQuarterData] = useState<QuarterData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compare mode state
  const [compareTickers, setCompareTickers] = useState<string[]>([]);
  const [compareData, setCompareData] = useState<Record<string, QuarterData[]>>({});
  const [compareLoading, setCompareLoading] = useState(false);

  useEffect(() => {
    if (tickers.length > 0 && !selectedTicker) {
      setSelectedTicker(tickers[0]);
    }
  }, [tickers]);

  useEffect(() => {
    if (selectedTicker) fetchHistorical(selectedTicker);
  }, [selectedTicker]);

  const fetchHistorical = async (ticker: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('earnings-calendar', {
        body: { mode: 'historical', ticker, limit: 16 },
      });
      if (fnError) throw fnError;
      if (!data?.success) throw new Error(data?.error || 'Failed');
      setQuarterData(data.quarters || []);
    } catch (e: any) {
      setError(e.message);
      setQuarterData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompareData = useCallback(async (tickerList: string[]) => {
    if (tickerList.length === 0) return;
    setCompareLoading(true);
    try {
      const results = await Promise.all(
        tickerList.map(async (ticker) => {
          if (compareData[ticker]) return { ticker, quarters: compareData[ticker] };
          const { data } = await supabase.functions.invoke('earnings-calendar', {
            body: { mode: 'historical', ticker, limit: 12 },
          });
          return { ticker, quarters: data?.quarters || [] };
        })
      );
      const newData: Record<string, QuarterData[]> = {};
      for (const r of results) {
        newData[r.ticker] = r.quarters;
      }
      setCompareData(newData);
    } finally {
      setCompareLoading(false);
    }
  }, [compareData]);

  const toggleCompareTicker = (ticker: string) => {
    setCompareTickers(prev => {
      const next = prev.includes(ticker) ? prev.filter(t => t !== ticker) : [...prev, ticker].slice(0, 8);
      // Fetch data for newly added tickers
      const newTickers = next.filter(t => !compareData[t]);
      if (newTickers.length > 0) {
        fetchCompareData(next);
      }
      return next;
    });
  };

  // Build compare chart data: merge all tickers by quarter label
  const buildCompareChartData = (field: 'surprisePct' | 'revenueSurprisePct' = 'surprisePct') => {
    if (compareTickers.length === 0) return [];
    const allLabels = new Set<string>();
    const tickerSurprises: Record<string, Record<string, number | null>> = {};

    for (const ticker of compareTickers) {
      const quarters = compareData[ticker] || [];
      tickerSurprises[ticker] = {};
      for (const q of quarters) {
        const val = q[field];
        if (val != null) {
          const label = `${q.period} ${q.calendarYear}`;
          allLabels.add(label);
          tickerSurprises[ticker][label] = val;
        }
      }
    }

    const sortedLabels = Array.from(allLabels).sort((a, b) => {
      const parse = (s: string) => {
        const parts = s.split(' ');
        const year = parseInt(parts[1]);
        const qNum = parseInt(parts[0].replace('Q', ''));
        return year * 4 + qNum;
      };
      return parse(a) - parse(b);
    });

    return sortedLabels.map(label => {
      const row: any = { label };
      for (const ticker of compareTickers) {
        row[ticker] = tickerSurprises[ticker]?.[label] ?? null;
      }
      return row;
    });
  };

  const chartData = quarterData
    .filter(q => q.epsEstimated != null && q.epsActual != null)
    .map(q => ({
      label: `${q.period} ${q.calendarYear}`,
      epsEstimated: q.epsEstimated,
      epsActual: q.epsActual,
      surprise: q.surprisePct,
      beat: q.beat,
    }));

  const revenueChartData = quarterData
    .filter(q => q.revenueEstimated != null && q.revenue != null)
    .map(q => ({
      label: `${q.period} ${q.calendarYear}`,
      revenueActual: q.revenue / 1e6,
      revenueEstimated: (q.revenueEstimated ?? 0) / 1e6,
      surprise: q.revenueSurprisePct,
      beat: q.revenueBeat,
    }));

  const beatsCount = chartData.filter(d => d.beat === true).length;
  const missesCount = chartData.filter(d => d.beat === false).length;
  const beatRate = chartData.length > 0 ? Math.round((beatsCount / chartData.length) * 100) : 0;
  const avgSurprise = chartData.length > 0
    ? (chartData.reduce((sum, d) => sum + (d.surprise || 0), 0) / chartData.length).toFixed(1)
    : '0';

  const revBeats = revenueChartData.filter(d => d.beat === true).length;
  const revMisses = revenueChartData.filter(d => d.beat === false).length;
  const revBeatRate = revenueChartData.length > 0 ? Math.round((revBeats / revenueChartData.length) * 100) : 0;
  const revAvgSurprise = revenueChartData.length > 0
    ? (revenueChartData.reduce((sum, d) => sum + (d.surprise || 0), 0) / revenueChartData.length).toFixed(1)
    : '0';

  const compareChartData = buildCompareChartData('surprisePct');
  const compareRevenueChartData = buildCompareChartData('revenueSurprisePct');

  // Combined EPS + Revenue surprise data
  const combinedData = quarterData
    .filter(q => q.surprisePct != null || q.revenueSurprisePct != null)
    .map(q => ({
      label: `${q.period} ${q.calendarYear}`,
      epsSurprise: q.surprisePct,
      revenueSurprise: q.revenueSurprisePct,
      epsBeat: q.beat,
      revenueBeat: q.revenueBeat,
    }));

  if (tickers.length === 0) return null;

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-orange-500" />
              Historical Earnings & Revenue Surprise Tracker
            </CardTitle>
            <CardDescription>Multi-quarter EPS and revenue beat/miss trends per stock</CardDescription>
          </div>
          <Select value={selectedTicker} onValueChange={setSelectedTicker}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Select ticker" />
            </SelectTrigger>
            <SelectContent>
              {tickers.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Loading historical earnings...</span>
          </div>
        ) : error ? (
          <p className="text-sm text-destructive text-center py-8">{error}</p>
        ) : chartData.length === 0 && revenueChartData.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No historical earnings surprise data available for {selectedTicker}.</p>
        ) : (
          <Tabs defaultValue="eps" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="eps" className="gap-1.5">
                <BarChart3 className="h-3.5 w-3.5" /> EPS Surprise
              </TabsTrigger>
              <TabsTrigger value="revenue" className="gap-1.5">
                <DollarSign className="h-3.5 w-3.5" /> Revenue Surprise
              </TabsTrigger>
              <TabsTrigger value="combined" className="gap-1.5">
                <Layers className="h-3.5 w-3.5" /> Combined
              </TabsTrigger>
              <TabsTrigger value="compare" className="gap-1.5">
                <GitCompareArrows className="h-3.5 w-3.5" /> Compare
              </TabsTrigger>
            </TabsList>

            {/* EPS Tab */}
            <TabsContent value="eps">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                  {beatsCount} Beats
                </Badge>
                <Badge variant="outline" className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30">
                  {missesCount} Misses
                </Badge>
                <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30">
                  Beat Rate: {beatRate}%
                </Badge>
                <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
                  Avg Surprise: {avgSurprise}%
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto gap-1.5"
                  disabled={chartData.length === 0}
                  onClick={() => {
                    const header = 'Quarter,EPS Actual,EPS Estimated,Surprise %,Beat\n';
                    const rows = chartData.map(d => `${d.label},${d.epsActual},${d.epsEstimated},${d.surprise},${d.beat}`).join('\n');
                    const blob = new Blob([header + rows], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `eps_surprise_${selectedTicker}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="h-3.5 w-3.5" /> Export CSV
                </Button>
              </div>
              <div className="mb-6">
                <p className="text-xs font-medium text-muted-foreground mb-2">EPS: Actual vs Estimated</p>
                <ResponsiveContainer width="100%" height={250}>
                  <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                    <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      labelStyle={{ color: 'hsl(var(--foreground))' }}
                      formatter={(value: any, name: string) => [`$${Number(value).toFixed(2)}`, name === 'epsActual' ? 'EPS Actual' : 'EPS Estimated']}
                    />
                    <Legend formatter={(value) => value === 'epsActual' ? 'EPS Actual' : 'EPS Estimated'} />
                    <Bar dataKey="epsEstimated" fill="hsl(var(--muted-foreground) / 0.3)" radius={[2, 2, 0, 0]} name="epsEstimated" />
                    <Line type="monotone" dataKey="epsActual" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} name="epsActual" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">EPS Surprise %</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                    <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" tickFormatter={(v) => `${v}%`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      formatter={(value: any) => [`${Number(value).toFixed(1)}%`, 'Surprise']}
                    />
                    <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                    <Bar dataKey="surprise" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={index} fill={entry.beat ? 'hsl(142 76% 36%)' : 'hsl(0 84% 60%)'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            {/* Revenue Tab */}
            <TabsContent value="revenue">
              {revenueChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No revenue estimate data available for {selectedTicker}.</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                      {revBeats} Beats
                    </Badge>
                    <Badge variant="outline" className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30">
                      {revMisses} Misses
                    </Badge>
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30">
                      Beat Rate: {revBeatRate}%
                    </Badge>
                    <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
                      Avg Surprise: {revAvgSurprise}%
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto gap-1.5"
                      disabled={revenueChartData.length === 0}
                      onClick={() => {
                        const header = 'Quarter,Revenue Actual (M),Revenue Estimated (M),Surprise %,Beat\n';
                        const rows = revenueChartData.map(d => `${d.label},${d.revenueActual.toFixed(1)},${d.revenueEstimated.toFixed(1)},${d.surprise},${d.beat}`).join('\n');
                        const blob = new Blob([header + rows], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `revenue_surprise_${selectedTicker}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <Download className="h-3.5 w-3.5" /> Export CSV
                    </Button>
                  </div>
                  <div className="mb-6">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Revenue: Actual vs Estimated (in millions)</p>
                    <ResponsiveContainer width="100%" height={250}>
                      <ComposedChart data={revenueChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                        <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" tickFormatter={(v) => `$${v.toFixed(0)}M`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                          labelStyle={{ color: 'hsl(var(--foreground))' }}
                          formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name === 'revenueActual' ? 'Revenue Actual' : 'Revenue Estimated']}
                        />
                        <Legend formatter={(value) => value === 'revenueActual' ? 'Revenue Actual' : 'Revenue Estimated'} />
                        <Bar dataKey="revenueEstimated" fill="hsl(var(--muted-foreground) / 0.3)" radius={[2, 2, 0, 0]} name="revenueEstimated" />
                        <Line type="monotone" dataKey="revenueActual" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} name="revenueActual" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Revenue Surprise %</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={revenueChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                        <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" tickFormatter={(v) => `${v}%`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                          formatter={(value: any) => [`${Number(value).toFixed(1)}%`, 'Surprise']}
                        />
                        <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                        <Bar dataKey="surprise" radius={[4, 4, 0, 0]}>
                          {revenueChartData.map((entry, index) => (
                            <Cell key={index} fill={entry.beat ? 'hsl(142 76% 36%)' : 'hsl(0 84% 60%)'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Compare Tab */}
            <TabsContent value="compare">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Select tickers to compare (up to 8)</p>
                  <div className="flex flex-wrap gap-3">
                    {tickers.map((t) => (
                      <label key={t} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <Checkbox
                          checked={compareTickers.includes(t)}
                          onCheckedChange={() => toggleCompareTicker(t)}
                        />
                        <span
                          className="font-medium"
                          style={{ color: compareTickers.includes(t) ? COMPARE_COLORS[compareTickers.indexOf(t) % COMPARE_COLORS.length] : undefined }}
                        >
                          {t}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                {compareTickers.length >= 2 && compareChartData.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    onClick={() => {
                      // Build CSV with EPS and Revenue surprise columns
                      const allLabels = new Set<string>();
                      compareChartData.forEach(r => allLabels.add(r.label));
                      compareRevenueChartData.forEach(r => allLabels.add(r.label));
                      const labels = Array.from(allLabels).sort();

                      const epsMap = new Map(compareChartData.map(r => [r.label, r]));
                      const revMap = new Map(compareRevenueChartData.map(r => [r.label, r]));

                      const epsHeaders = compareTickers.map(t => `${t} EPS Surprise %`);
                      const revHeaders = compareTickers.map(t => `${t} Rev Surprise %`);
                      const header = ['Quarter', ...epsHeaders, ...revHeaders].join(',');

                      const rows = labels.map(label => {
                        const epsRow = epsMap.get(label) || {};
                        const revRow = revMap.get(label) || {};
                        const epsCols = compareTickers.map(t => (epsRow as any)[t] ?? '');
                        const revCols = compareTickers.map(t => (revRow as any)[t] ?? '');
                        return [label, ...epsCols, ...revCols].join(',');
                      });

                      const csv = [header, ...rows].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `earnings_compare_${compareTickers.join('_')}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <Download className="h-3.5 w-3.5" /> Export CSV
                  </Button>
                )}
              </div>

              {compareLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading comparison data...</span>
                </div>
              ) : compareTickers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Select at least 2 tickers above to compare their surprise trends.</p>
              ) : compareChartData.length === 0 && compareRevenueChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No overlapping earnings data found for the selected tickers.</p>
              ) : (
                <>
                  {/* EPS Summary badges per ticker */}
                  <p className="text-xs font-semibold text-muted-foreground mb-2">EPS Beat Rates</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {compareTickers.map((ticker, i) => {
                      const tData = (compareData[ticker] || []).filter(q => q.surprisePct != null);
                      const beats = tData.filter(q => q.beat === true).length;
                      const rate = tData.length > 0 ? Math.round((beats / tData.length) * 100) : 0;
                      return (
                        <Badge key={ticker} variant="outline" style={{ borderColor: COMPARE_COLORS[i % COMPARE_COLORS.length], color: COMPARE_COLORS[i % COMPARE_COLORS.length] }}>
                          {ticker}: {rate}% ({beats}/{tData.length})
                        </Badge>
                      );
                    })}
                  </div>

                  {/* EPS Surprise Compare Chart */}
                  {compareChartData.length > 0 && (
                    <div className="mb-6">
                      <p className="text-xs font-medium text-muted-foreground mb-2">EPS Surprise % — Side by Side</p>
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={compareChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                          <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" tickFormatter={(v) => `${v}%`} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                            labelStyle={{ color: 'hsl(var(--foreground))' }}
                            formatter={(value: any, name: string) => [value != null ? `${Number(value).toFixed(1)}%` : 'N/A', name]}
                          />
                          <Legend />
                          <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                          {compareTickers.map((ticker, i) => (
                            <Line
                              key={ticker}
                              type="monotone"
                              dataKey={ticker}
                              stroke={COMPARE_COLORS[i % COMPARE_COLORS.length]}
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Revenue Summary badges per ticker */}
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Revenue Beat Rates</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {compareTickers.map((ticker, i) => {
                      const tData = (compareData[ticker] || []).filter(q => q.revenueSurprisePct != null);
                      const beats = tData.filter(q => q.revenueBeat === true).length;
                      const rate = tData.length > 0 ? Math.round((beats / tData.length) * 100) : 0;
                      return (
                        <Badge key={ticker} variant="outline" style={{ borderColor: COMPARE_COLORS[i % COMPARE_COLORS.length], color: COMPARE_COLORS[i % COMPARE_COLORS.length] }}>
                          {ticker}: {rate}% ({beats}/{tData.length})
                        </Badge>
                      );
                    })}
                  </div>

                  {/* Revenue Surprise Compare Chart */}
                  {compareRevenueChartData.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Revenue Surprise % — Side by Side</p>
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={compareRevenueChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                          <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" tickFormatter={(v) => `${v}%`} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                            labelStyle={{ color: 'hsl(var(--foreground))' }}
                            formatter={(value: any, name: string) => [value != null ? `${Number(value).toFixed(1)}%` : 'N/A', `${name} (Rev)`]}
                          />
                          <Legend formatter={(value) => `${value} (Rev)`} />
                          <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                          {compareTickers.map((ticker, i) => (
                            <Line
                              key={ticker}
                              type="monotone"
                              dataKey={ticker}
                              stroke={COMPARE_COLORS[i % COMPARE_COLORS.length]}
                              strokeWidth={2}
                              strokeDasharray="6 3"
                              dot={{ r: 4, strokeDasharray: '0' }}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* Combined Tab */}
            <TabsContent value="combined">
              {combinedData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No combined surprise data available for {selectedTicker}.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30">
                      EPS Beat Rate: {beatRate}%
                    </Badge>
                    <Badge variant="outline" className="bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/30">
                      Revenue Beat Rate: {revBeatRate}%
                    </Badge>
                    <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
                      Avg EPS Surprise: {avgSurprise}%
                    </Badge>
                    <Badge variant="outline" className="bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/30">
                      Avg Rev Surprise: {revAvgSurprise}%
                    </Badge>
                  </div>
                  <div className="mb-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">EPS vs Revenue Surprise % — Side by Side</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={combinedData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                        <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" tickFormatter={(v) => `${v}%`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                          labelStyle={{ color: 'hsl(var(--foreground))' }}
                          formatter={(value: any, name: string) => [
                            value != null ? `${Number(value).toFixed(1)}%` : 'N/A',
                            name === 'epsSurprise' ? 'EPS Surprise' : 'Revenue Surprise'
                          ]}
                        />
                        <Legend formatter={(value) => value === 'epsSurprise' ? 'EPS Surprise %' : 'Revenue Surprise %'} />
                        <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                        <Bar dataKey="epsSurprise" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="epsSurprise" barSize={16} />
                        <Line type="monotone" dataKey="revenueSurprise" stroke="hsl(142 76% 36%)" strokeWidth={2.5} dot={{ r: 5, fill: 'hsl(142 76% 36%)' }} name="revenueSurprise" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Beat/Miss grid */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Quarter-by-Quarter Beat/Miss</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {combinedData.map((d, i) => (
                        <div key={i} className="border rounded-md p-2 text-center text-xs bg-muted/30">
                          <p className="font-medium text-foreground mb-1">{d.label}</p>
                          <div className="flex justify-center gap-2">
                            <span className={d.epsBeat ? 'text-green-600 dark:text-green-400' : d.epsBeat === false ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}>
                              EPS {d.epsBeat ? '✓' : d.epsBeat === false ? '✗' : '—'}
                            </span>
                            <span className={d.revenueBeat ? 'text-green-600 dark:text-green-400' : d.revenueBeat === false ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}>
                              Rev {d.revenueBeat ? '✓' : d.revenueBeat === false ? '✗' : '—'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default EarningsSurpriseChart;
